import React, { useEffect, useMemo, useState } from 'react';
import ProfaseModal from '../../../commons/ProfaseModal';
import PageForm, { FormField } from '../../../commons/PageForm';
import PageAlert from '../../../commons/PageAlert';
import { useAppDispatch, useAppSelector } from '../../../store';
import { deleteServicioEnGrupo, getServicioEnGrupo, updateServicioEnGrupo } from '../grupo-empresarial.reducer';
import { proveedorIdFromRow } from '../proveedor/GrupoProveedorTable';
import {
  applyServicioAsignadoFieldChange,
  buildServicioAsignadoBaseFields,
  buildServicioAsignadoSubmitBody,
  categoriaServicioDescripcionFromApi,
  categoriaServicioLabelFromRow,
  categoriaServicioReadOnlyField,
  isCortesiaActiva,
  isInactivoActiva,
  makeEmptyServicioAsignadoForm,
  parseServicioHistorico,
  prefillServicioAsignadoForm,
  proveedorBloqueadoPorServicioApi,
  servicioAsignadoDisplayValues,
  validateServicioAsignadoForm,
  type ServicioAsignadoCrearFormValues,
  type ServicioHistoricoEntry,
} from './servicio-asignado-form';
import ServicioAsignadoHistoricoPanel from './ServicioAsignadoHistoricoPanel';
import {
  SERVICIO_ASIGNADO_FORM_ID,
  ServicioAsignadoModalActions,
  servicioAsignadoModalBodyGridStyle,
} from './ServicioAsignadoModalLayout';

const ACCENT = '#8b5cf6';

function apiErrorMessage(error: unknown, fallback: string): string {
  if (error == null) return fallback;
  const err = error as { response?: { data?: unknown }; data?: unknown; message?: string };
  const candidates = [err.response?.data, err.data, error];
  for (const data of candidates) {
    if (data == null || typeof data !== 'object') continue;
    const body = data as { detail?: unknown; message?: unknown; title?: unknown };
    const detail = body.detail != null ? String(body.detail).trim() : '';
    if (detail) return detail;
    const message = body.message != null ? String(body.message).trim() : '';
    if (message) return message;
    const title = body.title != null ? String(body.title).trim() : '';
    if (title) return title;
  }
  if (typeof err.message === 'string' && err.message.trim()) return err.message.trim();
  return fallback;
}

function proveedorOptionsFromRows(rows: any[] | undefined) {
  const seen = new Set<number>();
  const options: { value: string; label: string }[] = [];
  for (const row of rows ?? []) {
    const id = proveedorIdFromRow(row);
    if (id == null || seen.has(id)) continue;
    seen.add(id);
    const label = String(row.proveedor?.nombre ?? row.alias ?? `ID ${id}`).trim();
    options.push({ value: String(id), label: label || `ID ${id}` });
  }
  return options.sort((a, b) => a.label.localeCompare(b.label, 'es'));
}

export interface ServicioAsignadoEditarModalProps {
  open: boolean;
  servicioId: number | null;
  grupoId?: string | null;
  empresaId?: string | null;
  empresaNombre?: string;
  /** Fila del listado (etiqueta de categoría si el GET no trae descripción). */
  listRow?: any;
  proveedores?: any[];
  empresasGrupo?: any[];
  onClose: () => void;
  onSaved?: () => void;
  onDeleted?: () => void;
}

export default function ServicioAsignadoEditarModal({
  open,
  servicioId,
  grupoId,
  empresaId,
  empresaNombre,
  listRow,
  proveedores = [],
  empresasGrupo = [],
  onClose,
  onSaved,
  onDeleted,
}: ServicioAsignadoEditarModalProps) {
  const dispatch = useAppDispatch();
  const updating = useAppSelector(s => s.grupoEmpresarial.updating);

  const empresaIdNumerico =
    empresaId != null && empresaId !== 'new' ? Number(empresaId) : NaN;
  const defaultSeFacturaAId =
    !Number.isNaN(empresaIdNumerico) ? String(empresaIdNumerico) : '';

  const [form, setForm] = useState<ServicioAsignadoCrearFormValues>(() =>
    makeEmptyServicioAsignadoForm(defaultSeFacturaAId),
  );
  const [errors, setErrors] = useState<
    Partial<Record<keyof ServicioAsignadoCrearFormValues, string>>
  >({});
  const [categoriaLabel, setCategoriaLabel] = useState('');
  const [proveedorBloqueado, setProveedorBloqueado] = useState(false);
  const [historico, setHistorico] = useState<ServicioHistoricoEntry[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [ready, setReady] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const proveedorOptions = useMemo(
    () => proveedorOptionsFromRows(proveedores),
    [proveedores],
  );

  const empresaOptions = useMemo(
    () =>
      (empresasGrupo ?? [])
        .filter((e: any) => e?.id != null)
        .map((e: any) => ({
          value: String(e.id),
          label: String(e.razonSocial ?? e.nombre ?? `ID ${e.id}`).trim(),
        }))
        .sort((a, b) => a.label.localeCompare(b.label, 'es')),
    [empresasGrupo],
  );

  const cortesiaActiva = isCortesiaActiva(form);
  const inactivoActiva = isInactivoActiva(form);
  const tieneHistorico = historico.length > 0;
  const formInactivoStyle: React.CSSProperties | undefined = inactivoActiva
    ? { color: '#94a3b8', opacity: 0.78 }
    : undefined;

  const formFields = useMemo<FormField<ServicioAsignadoCrearFormValues>[]>(
    () =>
      buildServicioAsignadoBaseFields({
        proveedorOptions,
        empresaOptions,
        proveedorBloqueado,
        cortesiaActiva,
        categoriaField: categoriaServicioReadOnlyField(),
      }),
    [proveedorOptions, empresaOptions, proveedorBloqueado, cortesiaActiva],
  );

  const displayValues = useMemo(
    () => servicioAsignadoDisplayValues(form, categoriaLabel),
    [form, categoriaLabel],
  );

  useEffect(() => {
    if (!open || servicioId == null || grupoId == null || grupoId === 'new') {
      setReady(false);
      return;
    }
    if (Number.isNaN(empresaIdNumerico)) {
      setReady(false);
      return;
    }

    let cancelled = false;
    setReady(false);
    setLoadError(null);
    setForm(makeEmptyServicioAsignadoForm(defaultSeFacturaAId));
    setCategoriaLabel(listRow != null ? categoriaServicioLabelFromRow(listRow) : '');
    setProveedorBloqueado(false);
    setHistorico([]);
    setDeleteConfirmOpen(false);
    setDeleteError(null);

    dispatch(
      getServicioEnGrupo({
        grupoId,
        empresaId: empresaIdNumerico,
        servicioId,
      }),
    )
      .unwrap()
      .then(result => {
        if (cancelled) return;
        const data = result.data as Record<string, unknown>;
        const base = makeEmptyServicioAsignadoForm(defaultSeFacturaAId);
        setForm(prefillServicioAsignadoForm(data, base));
        setCategoriaLabel(
          categoriaServicioDescripcionFromApi(data) ||
            (listRow != null ? categoriaServicioLabelFromRow(listRow) : '') ||
            '—',
        );
        setProveedorBloqueado(proveedorBloqueadoPorServicioApi(data));
        setHistorico(parseServicioHistorico(data));
        setReady(true);
      })
      .catch(() => {
        if (cancelled) return;
        setLoadError('No se pudo cargar el servicio asignado.');
      });

    return () => {
      cancelled = true;
    };
  }, [
    open,
    servicioId,
    grupoId,
    empresaIdNumerico,
    defaultSeFacturaAId,
    listRow,
    dispatch,
  ]);

  const handleFieldChange = (name: keyof ServicioAsignadoCrearFormValues, value: string) => {
    if (name === 'categoriaServicioId') return;
    if (name === 'proveedorId' && proveedorBloqueado) return;
    setErrors(prev => ({ ...prev, [name]: undefined }));
    setForm(prev => applyServicioAsignadoFieldChange(prev, name, value));
  };

  const toggleInactivo = (checked: boolean) => {
    setErrors(prev => ({ ...prev, inactivo: undefined }));
    setForm(prev => ({ ...prev, inactivo: checked ? 'true' : 'false' }));
  };

  const handleDelete = async () => {
    if (tieneHistorico) return;
    const gid = Number(grupoId);
    const eid = empresaIdNumerico;
    if (Number.isNaN(gid) || Number.isNaN(eid) || servicioId == null) return;

    try {
      await dispatch(
        deleteServicioEnGrupo({
          grupoId: gid,
          empresaId: eid,
          servicioId,
        }),
      ).unwrap();
      onDeleted?.();
      onClose();
    } catch (err) {
      setDeleteConfirmOpen(false);
      setDeleteError(apiErrorMessage(err, 'No se pudo eliminar el servicio asignado.'));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errs = validateServicioAsignadoForm(form);
    if (!form.categoriaServicioId.trim()) {
      errs.categoriaServicioId = 'Categoría de servicio no disponible';
    }
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }

    const gid = Number(grupoId);
    const eid = empresaIdNumerico;
    if (Number.isNaN(gid) || Number.isNaN(eid) || servicioId == null) {
      return;
    }

    const body = buildServicioAsignadoSubmitBody(form);

    try {
      await dispatch(
        updateServicioEnGrupo({
          grupoId: gid,
          empresaId: eid,
          servicioId,
          body,
        }),
      ).unwrap();
      onSaved?.();
      onClose();
    } catch {
      /* 403 u otro error: interceptor / UI global */
    }
  };

  if (!open) return null;
  if (
    grupoId == null ||
    grupoId === 'new' ||
    Number.isNaN(empresaIdNumerico) ||
    servicioId == null
  ) {
    return null;
  }

  const subtitle = empresaNombre
    ? `Edite el servicio asignado a ${empresaNombre}.`
    : 'Edite el servicio asignado a la empresa seleccionada.';

  return (
    <ProfaseModal
      open
      titleId="servicio-asignado-editar-modal-title"
      size={ready ? 'large' : 'default'}
      zIndex={100010}
      dialogStyle={ready ? undefined : { maxWidth: 560, maxHeight: 'min(90vh, 720px)' }}
      onBackdropMouseDown={e => {
        if (e.target === e.currentTarget && !updating) onClose();
      }}
    >
      <div className="d-flex align-items-start justify-content-between gap-3 mb-2">
        <h2 id="servicio-asignado-editar-modal-title" className="profase-modal-title m-0">
          Editar servicio asignado
        </h2>
        <button
          type="button"
          className="profase-form-btn-secondary btn-sm flex-shrink-0"
          onClick={onClose}
          disabled={updating}
          aria-label="Cerrar"
        >
          Cerrar
        </button>
      </div>
      <p className="profase-modal-description">{subtitle}</p>
      {loadError ? (
        <p className="profase-form-alert-error" style={{ margin: 0 }} role="alert">
          {loadError}
        </p>
      ) : !ready ? (
        <p className="profase-form-muted-text m-0" style={{ fontSize: 13 }}>
          Cargando servicio…
        </p>
      ) : (
          <>
            {deleteError ? (
              <div style={{ marginBottom: 16 }}>
                <PageAlert
                  alert={{
                    kind: 'toast',
                    variant: 'error',
                    message: deleteError,
                    onDismiss: () => setDeleteError(null),
                  }}
                />
              </div>
            ) : deleteConfirmOpen ? (
              <div style={{ marginBottom: 16 }}>
                <PageAlert
                  alert={{
                    kind: 'confirm',
                    variant: 'warning',
                    message: `¿Confirma eliminar el servicio «${categoriaLabel || 'asignado'}»? Esta acción no se puede deshacer.`,
                    confirmLabel: 'Eliminar',
                    cancelLabel: 'Cancelar',
                    loading: updating,
                    onConfirm: () => void handleDelete(),
                    onCancel: () => setDeleteConfirmOpen(false),
                  }}
                />
              </div>
            ) : null}
            <div style={servicioAsignadoModalBodyGridStyle()}>
              <div style={formInactivoStyle}>
                <PageForm
                formId={SERVICIO_ASIGNADO_FORM_ID}
                fields={formFields}
                values={displayValues}
                errors={errors}
                isNew={false}
                saving={updating}
                labelInline
                hideActions
                onFieldChange={handleFieldChange}
                onSubmit={handleSubmit}
                onCancel={onClose}
                accentColor={ACCENT}
              />
              </div>
              <ServicioAsignadoHistoricoPanel
                entries={historico}
                inactivo={inactivoActiva}
                footer={
                  <div className="d-flex align-items-center justify-content-between gap-2 flex-wrap">
                    <label
                      className="d-flex align-items-center gap-2 mb-0"
                      style={{
                        fontSize: 13,
                        fontWeight: 600,
                        color: inactivoActiva ? '#94a3b8' : '#334155',
                        cursor: updating ? 'not-allowed' : 'pointer',
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={inactivoActiva}
                        disabled={updating}
                        onChange={e => toggleInactivo(e.target.checked)}
                      />
                      Inactivo
                    </label>
                    <button
                      type="button"
                      className="btn btn-sm btn-danger"
                      disabled={updating || tieneHistorico}
                      title={
                        tieneHistorico
                          ? 'No se puede borrar un servicio con histórico de precios'
                          : 'Eliminar servicio asignado'
                      }
                      onClick={() => {
                        setDeleteError(null);
                        setDeleteConfirmOpen(true);
                      }}
                    >
                      Borrar
                    </button>
                  </div>
                }
              />
            </div>
            <ServicioAsignadoModalActions
              formId={SERVICIO_ASIGNADO_FORM_ID}
              updating={updating}
              submitLabel="Guardar"
              onClose={onClose}
            />
          </>
        )}
    </ProfaseModal>
  );
}
